import type { GreyscaleImage } from './image.js';
import { binariseAdaptive, inkFraction, type BinaryImage } from './threshold.js';
import { estimateSkewDegrees, rotateBinary } from './deskew.js';

export interface PreprocessResult {
  readonly binary: BinaryImage;
  /** The skew found, in degrees, whether or not it was corrected. */
  readonly skewDegrees: number;
  readonly inkFraction: number;
}

/**
 * Binarise, measure the skew, correct it.
 *
 * The order is not interchangeable. Skew is estimated from ink, so binarisation
 * has to come first; and it is estimated *before* correction so the figure can be
 * reported rather than silently absorbed — an operator looking at a scan that came
 * out badly wants to know it was 2.8 degrees off square.
 *
 * **`deskew` is off for scoring, and that is a decision worth finding here rather
 * than in a bad accuracy figure.** Ground truth for a raster fixture is annotated
 * in the frame the image is actually in (`kernel/fixtures/raster_truth.py`), and
 * the generator produced that frame by rotating about the page ORIGIN and then
 * translating the bitmap onto its bounding box. Correcting the skew here rotates
 * about the IMAGE CENTRE, which is a different rigid motion: the two differ by
 * roughly `theta * |centre|`, about 15 px or 3.6 pt on a 300 DPI A4 sheet, against
 * a matching tolerance of 1.03 pt. So a deskewed detection cannot be scored
 * against either truth without a third transform, and the acceptance runner
 * therefore detects in the image's own frame and reports the estimated angle
 * beside the manifest's `skewDegrees` as its own measurement. Deskew is for the
 * operator's output, not for the scorer's frame.
 */
export function preprocess(
  image: GreyscaleImage,
  { deskew = true }: { deskew?: boolean } = {},
): PreprocessResult {
  const binarised = binariseAdaptive(image);
  const skewDegrees = estimateSkewDegrees(binarised);
  const binary = deskew && skewDegrees !== 0 ? rotateBinary(binarised, -skewDegrees) : binarised;
  return { binary, skewDegrees, inkFraction: inkFraction(binary) };
}
